import { X } from 'lucide-react';
import React from 'react';

import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/design-system/components/ui';
import { cn } from '@/design-system/lib/utils';

/**
 * Elements that render outside this dialog's DOM subtree but must never dismiss it.
 *
 * Radix portals (select/popper) mount at body level, and so do Dynamic Labs' wallet
 * overlays — `#dynamic-send-transaction`, `#dynamic-sign-message`,
 * `#dynamic-prompt-to-add-network`, and siblings. Dynamic renders its confirmation
 * modal for external wallets too (`shouldConfirmAction` does not branch on wallet
 * type), so the very click that approves a transaction was landing "outside" the
 * dialog and tearing it down mid-flow — FUU-1988.
 *
 * Match on the container id rather than anything inside it: Dynamic renders into a
 * shadow root, and an event crossing that boundary is retargeted to the shadow host.
 * The host is a child of the id container, which `closest()` resolves; a selector
 * aimed at inner elements would silently fail to match.
 */
const DISMISS_EXEMPT_SELECTOR =
  '[data-radix-select-content], [data-radix-popper-content-wrapper], [id^="dynamic-"], [id^="dyn-"]';

const preventExemptLayerDismiss = (event: Event) => {
  const target = event.target as HTMLElement | null;
  if (target?.closest(DISMISS_EXEMPT_SELECTOR)) {
    event.preventDefault();
  }
};

export interface SimpleModalProps {
  children: React.ReactNode;
  onClose: () => void;
  open: boolean;
  title?: React.ReactNode;
  actions?: React.ReactNode;
  className?: string;
  contentClassName?: string;
  disableAutoFocus?: boolean;
  disableRestoreFocus?: boolean;
}

export const SimpleModal = ({
  children,
  onClose,
  open,
  title,
  actions,
  className,
  contentClassName,
  disableAutoFocus,
  disableRestoreFocus,
}: SimpleModalProps): React.ReactElement => {
  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <DialogContent
        hideCloseButton
        className={cn(
          'tw:min-w-[90vw] tw:md:min-w-[800px] tw:max-w-[90vw] tw:md:max-w-[720px] tw:max-h-[90vh] tw:flex tw:flex-col tw:p-0',
          className,
        )}
        onOpenAutoFocus={disableAutoFocus ? (e) => e.preventDefault() : undefined}
        onCloseAutoFocus={disableRestoreFocus ? (e) => e.preventDefault() : undefined}
        // Prevent the modal from closing when interacting with Radix Select/Popper
        // portals or Dynamic wallet overlays rendered outside its DOM subtree.
        onPointerDownOutside={preventExemptLayerDismiss}
        onInteractOutside={preventExemptLayerDismiss}
      >
        <DialogHeader className="tw:flex tw:flex-row tw:items-center tw:justify-between tw:space-y-0 tw:p-5 tw:pb-0">
          {title &&
            (typeof title === 'string' ? (
              <DialogTitle className="tw:text-lg tw:font-semibold">{title}</DialogTitle>
            ) : (
              <div className="tw:flex-1">{title}</div>
            ))}
          <button
            onClick={onClose}
            className="tw:absolute tw:right-3 tw:top-3 tw:rounded-sm tw:p-1 tw:opacity-70 tw:transition-opacity tw:hover:opacity-100 tw:focus:outline-none tw:focus:ring-2 tw:focus:ring-fuul-blue tw:focus:ring-offset-2"
          >
            <X className="tw:h-5 tw:w-5 tw:text-gray-500" />
            <span className="tw:sr-only">Close</span>
          </button>
        </DialogHeader>
        <div
          className={cn(
            'tw:overflow-y-auto tw:overflow-x-hidden tw:overscroll-contain tw:p-5 sm:tw:px-12 sm:tw:pb-12',
            contentClassName,
          )}
        >
          {children}
        </div>
        {actions && <DialogFooter className="tw:p-5">{actions}</DialogFooter>}
      </DialogContent>
    </Dialog>
  );
};
